/**
 * 홈 첫 장면 엔진(marketing-landing, 시안 hero.js T2-원테이크): 스크롤하면 카드가 화면으로 펼쳐지고(장면 크기는 그대로,
 * 가장자리 마스크만 넓어짐) 드론 비행 → 사진 → 정사영상 → 3D 로 기울어짐까지 스크롤이 끌고 간다. 값은 스크롤을 부드럽게 따라간다.
 * 영상 없이 미리 받은 기울임 프레임 36장을 이웃끼리 섞어 캔버스에 그린다. CSS 변수 `--k`(펼침)·`--p`(진행)·`--f`(끝 녹아듦)는
 * 프레임마다 DOM 에 직접 쓴다(이동 빈도 값 규칙).
 *
 * 그림은 메인 스레드 밖에서 디코딩한다: 정사영상·프레임을 받아(`fetch`) `createImageBitmap` 으로 비트맵을 만들어 두고 캔버스에는
 * 비트맵만 그린다 — `<img>` 를 캔버스에 그리면 처음 그리는 순간 메인 스레드가 디코딩하느라 멈춘다(프레임 한 장에 수십 ms).
 * 프레임 비트맵은 보이는 부분만 잘라, 지금 프레임 앞뒤 창만큼만 쥔다(메모리 상한 안). 비트맵을 못 만드는 브라우저는 `<img>` 로 그린다.
 */
import { clamp, lerp } from "../scenes/sceneMath";
import { SITE_QUERY } from "src/domains/shared/constants/styles/site";
import { truncateNumber } from "src/domains/shared/utils/truncateNumber";
import { DRONE_FORWARD_DEG, droneMarkup } from "./storyDrone";

/** 정사영상 그림 틀(px)과 가로 길이(m) — 프레임도 같은 틀(그림 파일이 더 커도 이 틀에 맞춰 그린다) */
const IMAGE_FRAME: readonly [number, number] = [1836, 858];
const SITE_WIDTH_M = 52;
/** 카드 펼침에 쓰는 스크롤(화면 높이 비율), 기울임 구간(장면 시간 단위) */
const EXPAND_FRACTION = 0.3;
const RISE_DURATION = 4500;
/** 이야기 길이(화면 높이 배수)와 끝에 고정이 풀리며 녹아드는 구간 */
const STORY_LENGTH = 3.0;
const STORY_TAIL = 0.42;
/** 이야기 구간 전체(고정 + 녹아듦) 길이(화면 높이 배수) — 첫 장면 칸의 CSS 기본 높이가 같은 값을 써서 엔진이 붙어도 높이가 바뀌지 않는다 */
export const STORY_HEIGHT_FACTOR = STORY_LENGTH + STORY_TAIL;
/** 첫 그림을 그리면 무대에 다는 표시 — 무대가 미리보기 위로 부드럽게 나타난다 */
export const STAGE_READY_ATTR = "data-ready";
/** 값이 스크롤을 따라가는 비율(프레임당) */
const FOLLOW = 0.22;
const MAX_DPR = 2;
const BACKGROUND = "#EEF3FA";
/** 옅은 회색 지도: 어두운 톤을 흰색으로 이만큼 들어 올린다 */
const GRAY_LIFT = 0.05;
/** 기울임 프레임을 한 번에 받는 수 */
const FRAME_CONCURRENCY = 4;
/** 진행 방향을 재는 시간 간격(장면 시간) */
const HEADING_STEP_MS = 8;
/** 디코딩해 둔 프레임 비트맵 창의 반지름(지금 프레임 앞뒤 수)과 창 전체 메모리 상한 */
const BITMAP_WINDOW = 8;
const BITMAP_BUDGET_BYTES = 64 * 1024 * 1024;
const BYTES_PER_PIXEL = 4;

const seededRandom = (seed = 5) => {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
};

export interface StoryEngineOptions {
  /** 이야기 구역(높이 = 화면 + 이야기 길이) */
  root: HTMLElement;
  /** 머리말 아래 붙어 있는 무대 */
  pin: HTMLElement;
  /** 장면이 그려질 칸 — 엔진이 안을 채운다 */
  stage: HTMLElement;
  /** 위에서 본 정사영상 */
  topUrl: string;
  /** 기울임 프레임 i(0~count-1) 주소 */
  frameUrl: (index: number) => string;
  frameCount: number;
  reducedMotion: boolean;
}

export interface StoryEngine {
  dispose(): void;
}

interface Shot {
  x: number;
  y: number;
  t: number;
  r: number;
  element?: HTMLElement;
}

type Segment =
  | {
      kind: "pass";
      i: number;
      x1: number;
      x2: number;
      y: number;
      t0: number;
      duration: number;
    }
  | {
      kind: "turn";
      i: number;
      x: number;
      y1: number;
      y2: number;
      t0: number;
      duration: number;
      clockwise: boolean;
    };

/** 캔버스에 그릴 그림 — 메인 스레드 밖에서 디코딩한 비트맵이 기본, 못 만드는 브라우저는 `<img>` */
type Picture = ImageBitmap | HTMLImageElement;
/** 그릴 그림과 자리 — 비트맵은 보이는 부분만 잘라 둔 것이라 그 부분 자리에 그린다 */
interface Placed {
  picture: Picture;
  cropped: boolean;
}

const canDecodeOffThread = () =>
  typeof createImageBitmap === "function" && typeof fetch === "function";

const fetchBlob = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url} ${response.status}`);
  return response.blob();
};

/**
 * 사진 조각의 CSS 배경이 쓸 그림을 미리 디코딩한다 — 안 하면 큰 그림은 칠할 때 따로 디코딩돼 무대가 나타난 뒤 사진만 늦게 튀어나온다.
 * `crossOrigin` 을 달면 CSS 배경과 다른 요청이 되어 이 효과가 사라진다(실측).
 */
const decodeForBackground = async (url: string) => {
  const image = new Image();
  image.src = url;
  await image.decode().catch(() => {});
  return image;
};

/** 다음 프레임을 칠한 뒤에 부른다 — rAF 콜백은 칠하기 전에 돌아 두 번 기다린다 */
const afterNextPaint = (callback: () => void) =>
  requestAnimationFrame(() => requestAnimationFrame(callback));

/** 디코딩한 사진의 blob 주소와 그림 — 디코딩 결과가 캐시에서 밀려나지 않게 그림을 함께 붙들어 둔다 */
type DecodedPhoto = { url: string; image: HTMLImageElement };

/** 디코딩까지 마친 `<img>` — 비트맵을 못 만들 때의 대체 경로 */
const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";
    image.onload = () => {
      image
        .decode()
        .catch(() => {})
        .then(() => resolve(image));
    };
    image.onerror = reject;
    image.src = url;
  });

const closePicture = (picture: Picture | null | undefined) => {
  if (picture instanceof ImageBitmap) picture.close();
};

/** 그림 틀 기준으로 화면에 보이는 부분(비율) */
interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FrameBitmapsOptions {
  frameCount: number;
  crop: Crop;
  /** 받아 둔 프레임 원본 — 아직 없으면 undefined */
  blobAt: (index: number) => Blob | undefined;
  /** 무대가 살아 있고 이 창이 아직 그 무대의 것인지 — 아니면 완성된 비트맵을 버린다 */
  isCurrent: () => boolean;
  onReady: (index: number) => void;
  onFailed: (index: number) => void;
}

/** 창 전체가 메모리 상한 안에 들도록 반지름을 정한다 */
const windowSpanFor = (crop: Crop, size: { width: number; height: number }) => {
  const bytes =
    Math.ceil(crop.width * size.width) *
    Math.ceil(crop.height * size.height) *
    BYTES_PER_PIXEL;
  return Math.max(
    1,
    Math.min(BITMAP_WINDOW, Math.floor(BITMAP_BUDGET_BYTES / bytes / 2))
  );
};

/**
 * 프레임 비트맵 창 — 프레임 원본(blob)을 보이는 부분만 잘라 메인 스레드 밖에서 디코딩해 두고, 지금 프레임 앞뒤 창만큼만 쥔다.
 * 프레임 그림 크기는 첫 디코딩에서 알고, 그 크기로 창 반지름을 메모리 상한에 맞춘다.
 */
function createFrameBitmaps(options: FrameBitmapsOptions) {
  const { frameCount, crop, blobAt, isCurrent, onReady, onFailed } = options;
  const bitmaps: Array<ImageBitmap | undefined> = [];
  const pending = new Set<number>();
  const failed = new Set<number>();
  let frameSize: { width: number; height: number } | null = null;
  let frameSizePromise: Promise<void> | null = null;
  const span = () =>
    frameSize ? windowSpanFor(crop, frameSize) : BITMAP_WINDOW;
  const learnFrameSize = async (blob: Blob) => {
    const full = await createImageBitmap(blob);
    frameSize = { width: full.width, height: full.height };
    full.close();
  };
  const decode = async (blob: Blob) => {
    frameSizePromise ??= learnFrameSize(blob);
    await frameSizePromise;
    const size = frameSize as { width: number; height: number };
    return createImageBitmap(
      blob,
      Math.floor(crop.x * size.width),
      Math.floor(crop.y * size.height),
      Math.max(1, Math.ceil(crop.width * size.width)),
      Math.max(1, Math.ceil(crop.height * size.height))
    );
  };
  /** 창을 `center` 프레임 앞뒤로 옮긴다 — 안은 디코딩해 두고 밖은 놓아준다 */
  const ensureWindow = (center: number) => {
    const from = Math.max(0, Math.floor(center) - span());
    const to = Math.min(frameCount - 1, Math.ceil(center) + span());
    for (let i = 0; i < bitmaps.length; i++) {
      const outsideWindow = i < from || i > to;
      if (bitmaps[i] && outsideWindow) {
        bitmaps[i]?.close();
        bitmaps[i] = undefined;
      }
    }
    for (let i = from; i <= to; i++) {
      const blob = blobAt(i);
      const alreadyHandled = bitmaps[i] || pending.has(i) || failed.has(i);
      if (!blob || alreadyHandled) continue;
      pending.add(i);
      void decode(blob)
        .then((bitmap) => {
          pending.delete(i);
          const outsideWindowNow = i < from || i > to;
          if (!isCurrent() || outsideWindowNow) {
            bitmap.close();
            return;
          }
          bitmaps[i] = bitmap;
          onReady(i);
        })
        .catch(() => {
          pending.delete(i);
          failed.add(i);
          onFailed(i);
        });
    }
  };
  /** 프레임 j 의 비트맵, 없으면 창 안에서 가장 가까운 프레임의 비트맵 */
  const nearest = (j: number): ImageBitmap | undefined => {
    const exact = bitmaps[j];
    if (exact) return exact;
    for (let distance = 1; distance <= span(); distance++) {
      const near = bitmaps[j - distance] ?? bitmaps[j + distance];
      if (near) return near;
    }
    return undefined;
  };
  const release = () => {
    bitmaps.forEach((bitmap) => bitmap?.close());
    bitmaps.length = 0;
  };
  return { ensureWindow, nearest, exact: (j: number) => bitmaps[j], release };
}

export function createStoryEngine(options: StoryEngineOptions): StoryEngine {
  const { root, pin, stage, topUrl, frameUrl, frameCount, reducedMotion } =
    options;
  const offThread = canDecodeOffThread();
  let top: Picture | null = null;
  /** 사진 조각(`.fp`)의 바탕 그림 주소 — 정사영상을 받으면 그 blob 주소로 바꿔 같은 파일을 한 번 더 받지 않는다 */
  let photoSourceUrl = topUrl;
  let photo: DecodedPhoto | null = null;
  /** 비트맵을 만들 프레임 원본 — 창이 움직이면 다시 디코딩한다 */
  const frameBlobs: Array<Blob | undefined> = [];
  /** 비트맵을 못 만드는 브라우저(또는 받기에 실패한 프레임)의 `<img>` */
  const frameImages: Array<HTMLImageElement | undefined> = [];
  let grayCanvas: HTMLCanvasElement | null = null;
  let disposed = false;

  // Safari 는 canvas filter 를 지원하지 않으므로 옅은 회색 지도는 혼합 모드로 만든다 — 채도 0 의 회색을 saturation 모드로 덮으면 밝기만
  // 남는다(화소를 하나씩 돌지 않아 메인 스레드를 막지 않는다). 그 위에 흰색을 얹어 어두운 톤을 들어 올린다
  const makeGray = () => {
    if (!top) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = IMAGE_FRAME[0];
      canvas.height = IMAGE_FRAME[1];
      const context = canvas.getContext("2d");
      if (!context) return;
      context.drawImage(top, 0, 0, canvas.width, canvas.height);
      context.globalCompositeOperation = "saturation";
      context.fillStyle = "#808080";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.globalCompositeOperation = "source-over";
      context.fillStyle = `rgba(255,255,255,${GRAY_LIFT})`;
      context.fillRect(0, 0, canvas.width, canvas.height);
      grayCanvas = canvas;
    } catch {
      grayCanvas = null;
    }
  };
  let shown = false;
  /**
   * 첫 그림을 안 보이는 채 먼저 칠하고 다음 프레임에 페이드를 시작한다 — 칠하는 동안 페이드 시계가 돌면 첫 프레임부터 반쯤 나타나
   * 튀어나와 보인다(실측: 첫 프레임 불투명도 .62 → .13). `will-change` 가 없으면 페이드가 시작돼야 칠해 같은 일이 생긴다.
   */
  const markShown = () => {
    if (shown) return;
    shown = true;
    stage.style.willChange = "opacity";
    afterNextPaint(() => {
      stage.style.willChange = "";
      if (!disposed) stage.setAttribute(STAGE_READY_ATTR, "");
    });
  };

  /** 정사영상 그림과, 받은 blob 이 있으면 사진 조각이 같이 쓸 그 blob 주소 */
  const loadTop = async (): Promise<{
    picture: Picture;
    photo: DecodedPhoto | null;
  }> => {
    if (offThread) {
      try {
        const blob = await fetchBlob(topUrl);
        const picture = await createImageBitmap(blob);
        const url = URL.createObjectURL(blob);
        return {
          picture,
          photo: { url, image: await decodeForBackground(url) },
        };
      } catch {
        // 받기·디코딩에 실패하면 `<img>` 로
      }
    }
    return { picture: await loadImage(topUrl), photo: null };
  };

  // 기울임 프레임은 정사영상이 온 뒤 앞에서부터 차례로 받는다(기울임은 마지막 구간이라 그 전에 다 도착한다)
  const loadFrames = async () => {
    let next = 0;
    const worker = async () => {
      while (!disposed && next < frameCount) {
        const index = next++;
        try {
          if (offThread) frameBlobs[index] = await fetchBlob(frameUrl(index));
          else frameImages[index] = await loadImage(frameUrl(index));
        } catch {
          try {
            frameImages[index] = await loadImage(frameUrl(index));
          } catch {
            // 못 받은 프레임은 가장 가까운 프레임으로 그린다
          }
        }
        if (disposed) return;
        scene.ensureBitmapWindow(timeAt(current));
        // 프레임을 쓰는 구간에 있을 때만 다시 그린다 — 받을 때마다 전체를 다시 그리면 그 사이 스크롤이 끊긴다
        if (scene.usesFrames(timeAt(current))) {
          scene.lastKey = "";
          render(current);
        }
      }
    };
    await Promise.all(Array.from({ length: FRAME_CONCURRENCY }, worker));
  };

  const build = () => {
    stage.innerHTML = `<canvas></canvas><div class="foot"></div><svg class="path" aria-hidden="true"><path/></svg><div class="live"></div>${droneMarkup()}`;
    const W = stage.clientWidth;
    const H = stage.clientHeight;
    const canvas = stage.querySelector("canvas") as HTMLCanvasElement;
    // 고해상도 화면에서는 캔버스를 화면 픽셀 비율(최대 2배)로 그려 정사영상이 선명하게 보인다
    const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    const context = canvas.getContext("2d") as CanvasRenderingContext2D;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    const scale = Math.max(W / IMAGE_FRAME[0], H / IMAGE_FRAME[1]);
    const IW = IMAGE_FRAME[0] * scale;
    const IH = IMAGE_FRAME[1] * scale;
    const OX = (W - IW) / 2;
    const OY = (H - IH) / 2;
    const pixelsPerM = IW / SITE_WIDTH_M;
    const widthM = W / pixelsPerM;
    const foot = stage.querySelector(".foot") as HTMLElement;
    const pathSvg = stage.querySelector("svg.path") as SVGSVGElement;
    const pathElement = pathSvg.querySelector("path") as SVGPathElement;
    const live = stage.querySelector(".live") as HTMLElement;
    const drone = stage.querySelector(".drone") as HTMLElement;
    const heading = stage.querySelector(".drone .heading") as HTMLElement;
    const random = seededRandom(5);
    const PASSES = 3;
    // 사진 한 장 = 가로 9 m, 세로는 비행 줄 간격의 83%
    const FW = 9 * pixelsPerM;
    const FH = (H / 3) * 0.83;
    const photoCount = Math.max(3, Math.round((widthM - 8) / 5.5) + 1);
    const xs = Array.from(
      { length: photoCount },
      (_, k) => W / 2 + (k - (photoCount - 1) / 2) * 5.5 * pixelsPerM
    );
    const rows = [1, 2, 3].map((i) => (H * (i - 0.5)) / 3);
    const XA = -30;
    const XB = W + 30;
    const PASS_MS = 1900;
    const TURN_MS = 520;
    const R = H / 6;
    const segments: Segment[] = [];
    const shots: Shot[] = [];
    let d = "";
    for (let i = 0; i < PASSES; i++) {
      const leftToRight = i % 2 === 0;
      const x1 = leftToRight ? XA : XB;
      const x2 = leftToRight ? XB : XA;
      const y = rows[i];
      d += (i ? "" : `M${x1} ${y}`) + `L${x2} ${y}`;
      segments.push({
        kind: "pass",
        i,
        x1,
        x2,
        y,
        t0: i * (PASS_MS + TURN_MS),
        duration: PASS_MS,
      });
      if (i < PASSES - 1) {
        d += `A${R} ${R} 0 0 ${leftToRight ? 1 : 0} ${x2} ${rows[i + 1]}`;
        segments.push({
          kind: "turn",
          i,
          x: x2,
          y1: y,
          y2: rows[i + 1],
          t0: i * (PASS_MS + TURN_MS) + PASS_MS,
          duration: TURN_MS,
          clockwise: leftToRight,
        });
      }
    }
    pathElement.setAttribute("d", d);
    // 지나온 자리만 보이게 자르는 다각형은 무대 좌표라 svg 상자에 건다 — path 에 걸면 기준이 path 의 경계 상자가 돼 어긋난다
    pathSvg.style.clipPath = "polygon(0 0, 0 0, 0 0)";
    const flyEnd = (PASSES - 1) * (PASS_MS + TURN_MS) + PASS_MS;
    for (let i = 0; i < PASSES; i++) {
      const leftToRight = i % 2 === 0;
      for (const x of xs) {
        const u = (x - XA) / (XB - XA);
        shots.push({
          x,
          y: rows[i],
          t: i * (PASS_MS + TURN_MS) + (leftToRight ? u : 1 - u) * PASS_MS,
          r: (random() - 0.5) * 4,
        });
      }
    }
    shots.sort((a, b) => a.t - b.t);
    shots.forEach((shot) => {
      const element = document.createElement("i");
      element.className = "fp";
      element.style.cssText = `left:${shot.x}px;top:${
        shot.y
      }px;width:${FW}px;height:${FH}px;--r:${truncateNumber(
        shot.r,
        2
      )}deg;background-image:url(${photoSourceUrl});background-size:${IW}px ${IH}px;background-position:${
        OX - (shot.x - FW / 2)
      }px ${OY - (shot.y - FH / 2)}px`;
      shot.element = element;
      foot.append(element);
    });
    live.style.width = `${FW}px`;
    live.style.height = `${FH}px`;
    const positionAt = (t: number) => {
      for (const segment of segments) {
        if (t < segment.t0 + segment.duration) {
          const u = clamp((t - segment.t0) / segment.duration, 0, 1);
          if (segment.kind === "pass")
            return {
              x: lerp(segment.x1, segment.x2, u),
              y: segment.y,
              segment,
            };
          const cy = (segment.y1 + segment.y2) / 2;
          const a = -Math.PI / 2 + Math.PI * u;
          return {
            x: segment.x + (segment.clockwise ? 1 : -1) * R * Math.cos(a),
            y: cy + R * Math.sin(a),
            segment,
          };
        }
      }
      return {
        x: XB + 300 * ((t - flyEnd) / 700),
        y: rows[PASSES - 1],
        segment: null,
      };
    };
    /** 경로의 진행 방향(화면 각도: 오른쪽 0°, 아래 90°) — 조금 뒤 위치와의 차. 시작 전에는 첫 통과 방향 */
    const headingAt = (t: number) => {
      const from = positionAt(Math.max(t, 0));
      const to = positionAt(Math.max(t, 0) + HEADING_STEP_MS);
      return (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
    };
    const tMerge = flyEnd + 200;
    const t3d = tMerge + 2400;
    const tEnd = t3d + RISE_DURATION;
    /** 첫 통과(왼쪽→오른쪽)에서 드론이 x 에 닿는 시각 */
    const timeAtFirstPassX = (x: number) => ((x - XA) / (XB - XA)) * PASS_MS;
    // 첫 장면: 데스크톱은 사진 두 장이 놓이고 드론이 그 바로 앞(사진 두 장 + 촬영 틀). 세로 휴대폰은 첫 사진과 둘째 사진 사이 —
    // 가운데 로고 왼쪽에서 출발해 로고를 가리지 않는다
    const t0 = window.matchMedia(SITE_QUERY.phone).matches
      ? timeAtFirstPassX((shots[0].x + shots[1].x) / 2)
      : shots[1].t + 120;
    /** 기울임 진행(프레임 번호, 소수) — 기울임 전이면 0 */
    const frameAt = (t: number) =>
      clamp(((t - t3d) / RISE_DURATION) * (frameCount - 1), 0, frameCount - 1);

    // 프레임 비트맵은 화면에 보이는 부분(그림 틀 기준 비율)만 잘라 둔다 — 세로 화면에서는 가로의 1/4 정도라 메모리가 그만큼 준다
    const crop: Crop = {
      x: clamp(-OX / IW, 0, 1),
      y: clamp(-OY / IH, 0, 1),
      width: Math.min(1, W / IW),
      height: Math.min(1, H / IH),
    };
    const CX = OX + crop.x * IW;
    const CY = OY + crop.y * IH;
    const CW = crop.width * IW;
    const CH = crop.height * IH;
    const frameBitmaps = createFrameBitmaps({
      frameCount,
      crop,
      blobAt: (index) => frameBlobs[index],
      // 그 사이 무대를 다시 만들었으면(크기 바뀜) 이 창은 버려진 것이다
      isCurrent: () => !disposed && scene === state,
      onReady: () => {
        if (state.usesFrames(timeAt(current))) {
          state.lastKey = "";
          render(current);
        }
      },
      // 디코딩이 안 되는 프레임은 `<img>` 로 그린다
      onFailed: (index) => {
        void loadImage(frameUrl(index))
          .then((image) => {
            if (!disposed) frameImages[index] = image;
          })
          .catch(() => {});
      },
    });
    const nearestImage = (j: number) => {
      let k = j;
      while (k >= 0 && !frameImages[k]) k--;
      return k >= 0 ? frameImages[k] : undefined;
    };
    /** 프레임 j 로 그릴 그림: 그 비트맵 → 창 안의 가장 가까운 비트맵 → `<img>` → 가장 가까운 앞 `<img>` */
    const nearestFramePicture = (j: number): Placed | undefined => {
      const bitmap = frameBitmaps.nearest(j);
      if (bitmap) return { picture: bitmap, cropped: true };
      const image = frameImages[j] ?? nearestImage(j);
      return image ? { picture: image, cropped: false } : undefined;
    };
    const topPicture = (): Placed | undefined =>
      top ? { picture: top, cropped: false } : undefined;
    const drawPicture = ({ picture, cropped }: Placed) => {
      if (cropped) context.drawImage(picture, CX, CY, CW, CH);
      else context.drawImage(picture, OX, OY, IW, IH);
    };
    // 그림이 오기 전에는 캔버스를 비워 두어 아래 미리보기 그림이 비친다(흰 화면 없이 시작)
    const clear = () => context.clearRect(0, 0, W, H);
    const draw = (placed: Placed | undefined, alpha: number, gray: number) => {
      if (!placed) {
        clear();
        return false;
      }
      context.clearRect(0, 0, W, H);
      context.fillStyle = BACKGROUND;
      context.fillRect(0, 0, W, H);
      context.save();
      if (gray > 0.01 && grayCanvas) {
        context.globalAlpha = alpha * gray;
        context.drawImage(grayCanvas, OX, OY, IW, IH);
        if (gray < 0.99) {
          context.globalAlpha = alpha * (1 - gray);
          drawPicture(placed);
        }
      } else {
        context.globalAlpha = alpha;
        drawPicture(placed);
      }
      context.restore();
      markShown();
      return true;
    };
    // 기울임: 이웃한 두 프레임을 스크롤 비율로 섞어 그린다(프레임 사이가 끊겨 보이지 않게)
    const drawRise = (x: number) => {
      const i = Math.floor(x);
      const f = x - i;
      const A = nearestFramePicture(i) ?? topPicture();
      const B = nearestFramePicture(Math.min(frameCount - 1, i + 1));
      if (!A) {
        clear();
        return false;
      }
      context.clearRect(0, 0, W, H);
      context.fillStyle = BACKGROUND;
      context.fillRect(0, 0, W, H);
      context.save();
      context.globalAlpha = 1;
      drawPicture(A);
      const blendsNext = B !== undefined && B.picture !== A.picture && f > 0.01;
      if (blendsNext) {
        context.globalAlpha = f;
        drawPicture(B);
      }
      context.restore();
      markShown();
      return true;
    };
    const state = {
      /** 이 장면을 지은 무대 크기 */
      width: W,
      height: H,
      lastKey: "",
      t0,
      tEnd,
      /** 이 시각부터 기울임 프레임(완성 뒤 색 지도 포함)을 그린다 */
      usesFrames: (t: number) => t >= tMerge,
      /** 비트맵 창을 이 시각의 프레임 앞뒤로 옮긴다 */
      ensureBitmapWindow: (t: number) => frameBitmaps.ensureWindow(frameAt(t)),
      release: frameBitmaps.release,
      /** 사진 조각의 바탕 그림을 바꾼다(정사영상 blob 이 온 뒤) */
      setPhotoSource(url: string) {
        shots.forEach((shot) => {
          if (shot.element) shot.element.style.backgroundImage = `url(${url})`;
        });
      },
      apply(t: number) {
        const p = positionAt(t);
        const flying = t < flyEnd + 700;
        const fadeIn = clamp(t / 300, 0, 1);
        const fadeOut =
          t > flyEnd + 300 ? clamp(1 - (t - flyEnd - 300) / 400, 0, 1) : 1;
        drone.style.opacity = String(flying ? fadeIn * fadeOut : 0);
        if (flying) {
          drone.style.transform = `translate(${p.x}px,${p.y}px) translate(-50%,-50%)`;
          heading.style.transform = `rotate(${truncateNumber(
            headingAt(t) - DRONE_FORWARD_DEG,
            1
          )}deg)`;
          live.style.transform = drone.style.transform;
        }
        live.style.opacity = t < flyEnd && t > 0 ? "0.9" : "0";
        let clip = "none";
        const segment = p.segment;
        if (t <= 0) clip = "polygon(0 0, 0 0, 0 0)";
        else if (segment && segment.kind === "pass") {
          const yt = segment.y - 60;
          const yb = segment.y + 60;
          clip =
            segment.i % 2 === 0
              ? `polygon(-100px -100px, ${W + 100}px -100px, ${
                  W + 100
                }px ${yt}px, ${p.x}px ${yt}px, ${
                  p.x
                }px ${yb}px, -100px ${yb}px)`
              : `polygon(-100px -100px, ${W + 100}px -100px, ${
                  W + 100
                }px ${yb}px, ${p.x}px ${yb}px, ${
                  p.x
                }px ${yt}px, -100px ${yt}px)`;
        } else if (segment)
          clip = `polygon(-100px -100px, ${W + 100}px -100px, ${W + 100}px ${
            p.y + 10
          }px, -100px ${p.y + 10}px)`;
        pathSvg.style.clipPath = clip;
        pathSvg.style.opacity = t >= tMerge ? "0" : "1";
        shots.forEach((shot) => {
          shot.element?.classList.toggle("on", t >= shot.t);
          shot.element?.classList.toggle("set", t >= tMerge);
          shot.element?.classList.toggle("off", t >= tMerge + 900);
        });
        frameBitmaps.ensureWindow(frameAt(t));
        // 바탕: 옅은 회색 정사영상 → (완성 뒤 0.5초 구간) 온전한 색 → 기울임 프레임
        if (t >= t3d) {
          const x = frameAt(t);
          const key = `r${truncateNumber(x, 3)}`;
          if (key !== state.lastKey && drawRise(x)) state.lastKey = key;
        } else {
          // 색 지도는 기울임 첫 프레임과 같은 그림을 써서 전환 순간 화질 차가 없다. 첫 장면은 거의 흑백, 어둡게
          let placed = topPicture();
          let alpha = 0.9;
          let gray = 0.85;
          let key = "m0";
          if (t >= tMerge) {
            const k = clamp((t - tMerge) / 500, 0, 1);
            alpha = 0.9 + 0.1 * k;
            gray = 0.85 * (1 - k);
            key = `m${Math.round(k * 24)}`;
            const first = frameBitmaps.exact(0) ?? frameImages[0];
            if (first) {
              placed = {
                picture: first,
                cropped: first instanceof ImageBitmap,
              };
              key += "f";
            }
          }
          if (key !== state.lastKey && draw(placed, alpha, gray))
            state.lastKey = key;
        }
      },
    };
    return state;
  };

  let scene = build();
  let length = 0;
  let tail = 0;
  let target = 0;
  let current = 0;
  let frameId = 0;
  const measure = () => {
    const viewport = pin.clientHeight;
    length = Math.round(viewport * STORY_LENGTH);
    tail = Math.round(viewport * STORY_TAIL);
    root.style.setProperty("--story", `${length + tail}px`);
  };
  const timeAt = (scrolled: number) =>
    scene.t0 + clamp(scrolled / length, 0, 1) * (scene.tEnd - scene.t0);
  const render = (scrolled: number) => {
    const expand = clamp(scrolled / (pin.clientHeight * EXPAND_FRACTION), 0, 1);
    pin.style.setProperty("--k", truncateNumber(expand, 4));
    const progress = clamp(scrolled / length, 0, 1);
    scene.apply(timeAt(scrolled));
    // 진행도: 첫 장면의 푸른 그라데이션을 스크롤할수록 걷는 데 쓴다
    pin.style.setProperty("--p", truncateNumber(progress, 3));
    // 이야기 끝 뒤 고정이 풀리는 구간: 글은 사라지고 아래쪽이 흰색으로 녹아든다
    pin.style.setProperty(
      "--f",
      truncateNumber(clamp((scrolled - length) / Math.max(1, tail), 0, 1), 3)
    );
  };
  const tick = () => {
    frameId = 0;
    const delta = target - current;
    if (Math.abs(delta) > 0.5) {
      current += delta * FOLLOW;
      frameId = requestAnimationFrame(tick);
    } else {
      current = target;
    }
    render(current);
  };
  const kick = () => {
    if (!frameId) frameId = requestAnimationFrame(tick);
  };
  // 이야기 구역은 머리말 뒤까지 올라가 있어 구역 위가 화면 위에 닿는 순간이 0 이다
  const offsetTop = () => root.getBoundingClientRect().top;
  const handleScroll = () => {
    target = clamp(-offsetTop(), 0, length + tail);
    if (reducedMotion) current = target;
    kick();
  };
  measure();
  handleScroll();
  if (reducedMotion) current = target;
  window.addEventListener("scroll", handleScroll, { passive: true });
  let resizeTimer = 0;
  const handleResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      // 휴대폰 주소창이 접히고 펴질 때마다 resize 가 오지만 무대(svh) 크기는 그대로다 — 다시 지으면 깔린 사진이 모두 새 요소가 되어 번쩍인다
      if (
        stage.clientWidth === scene.width &&
        stage.clientHeight === scene.height
      )
        return;
      scene.release();
      measure();
      current = target = clamp(-offsetTop(), 0, length + tail);
      scene = build();
      // 새 사진 요소의 스타일이 계산되기 전(레이아웃을 읽기 전)에 놓인 상태를 달아야 불투명도 전환이 돌지 않는다
      scene.apply(timeAt(current));
      render(current);
    }, 200);
  };
  window.addEventListener("resize", handleResize);
  void loadTop()
    .then((loaded) => {
      if (disposed) {
        closePicture(loaded.picture);
        if (loaded.photo) URL.revokeObjectURL(loaded.photo.url);
        return;
      }
      top = loaded.picture;
      if (loaded.photo) {
        photo = loaded.photo;
        photoSourceUrl = loaded.photo.url;
        scene.setPhotoSource(loaded.photo.url);
      }
      makeGray();
      scene.lastKey = "";
      render(current);
      void loadFrames();
    })
    .catch(() => {
      // 정사영상을 못 받으면 미리보기 그림만 보인다
    });

  return {
    dispose() {
      disposed = true;
      cancelAnimationFrame(frameId);
      window.clearTimeout(resizeTimer);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      scene.release();
      closePicture(top);
      top = null;
      if (photo) URL.revokeObjectURL(photo.url);
      stage.innerHTML = "";
    },
  };
}
